<template>
	<template v-if="isDeleted">
		<BasketLineItemSkeleton />
	</template>
	<li
		v-if="!isDeleted"
		class="c-basket-line-item c-global-basket__product"
		data-testid="basketLineItem"
	>
		<div class="c-global-basket__product-header">
			<div>
				<DsText class="c-global-basket__product-manufacturer" htmlElement="span" text-type="body-xs" text-style="regular">
					{{ item.manufacturer }}
				</DsText>
				<DsText htmlElement="h3" text-type="heading-lg" text-style="bold" data-testid="line-item-name">
					<template v-if="disableLinkToProductDetails">
						{{ item.name }}
					</template>
					<template v-else>
						<a :href="productDetailsLink" class="c-global-basket__product-link" :aria-label="productNameAriaLabel">
						{{ item.name }}
						</a>
					</template>
				</DsText>
			</div>

			<div>
				<DsIconButton
					@click.prevent="removeItem"
					:title="removeItemAriaLabel"
					data-btn-remove
					icon-name="delete"
					variant="ghost-neutral"
					size="sm"
					class="c-global-basket__remove-btn"
					:aria-label="removeItemAriaLabel" />
			</div>
		</div>
		<div class="c-global-basket__product-details" :class="[size, additionalClasses]">
			<figure class="c-global-basket__product-figure" data-testid="line-item-product-figure">
				<template v-if="disableLinkToProductDetails">
					<img
						class="c-global-basket__product-image"
						:src="item.imageUrl"
						:alt="item.name"
						v-if="item.imageUrl" />
					<gallery-manufacturer-image
						v-else
						:manufacturer-data="manufactureInfo"
						:is-basket="true" />
				</template>
				<template v-else>
					<a
						:href="productDetailsLink"
						tabindex="-1"
						disabled
						class="c-global-basket__product-figure--link">
						<img
							class="c-global-basket__product-image"
							:src="item.imageUrl"
							:alt="item.name"
							v-if="item.imageUrl" />
						<gallery-manufacturer-image
							v-else
							:manufacturer-data="manufactureInfo"
							:is-basket="true" />
					</a>
				</template>
			</figure>

			<section class="c-global-basket__product-text">
				<!-- test case #1 -->
				<DsText v-if="showLineItemDescription(item)" class="c-global-basket__product-description" htmlElement="h4" text-type="body-sm" text-style="regular" data-testid="line-item-description">
					{{ item.description }}
				</DsText>

				<!-- test case #2 -->
				<DsText v-if="showLineItemColor(item)" class="c-global-basket__product-color" htmlElement="h4" text-type="body-sm" text-style="regular" data-testid="line-item-color">
						{{ labels.colorLabel }}: {{ item.color }}
				</DsText>

				<!-- test case #3 -->
				<DsText v-if="showLineItemPacksSection()" htmlElement="div" text-type="body-sm" text-style="regular" class="c-global-basket__product-packs" data-testid="line-item-packs">
					<span v-if="showLineItemPerPackQuantity()">{{ packsOfLabel }} {{ item.itemsPerPack }}</span>
					<span v-if="showLineItemPerPackPrice()" class="c-global-basket__product-packs__pack" data-testid="line-item-price-per-pack">
						{{ item.formattedPerPackPrice }} {{ labels.packPreposition || '/' }}
						{{ labels.packLabel }}
					</span>
				</DsText>

				<!-- test case #4 -->
				<DsText v-if="showLineItemSalesTaxIncludedLabel(item)" html-element="div" text-style="regular" text-type="body-xs" class="c-global-basket__product-tax" data-testid="line-item-sales-tax-label">
					{{ labels.salesTaxIncludedLabel }}
				</DsText>

				<!-- test case #5 -->
				<DsText v-if="showLineItemSampleQuantity(item)" class="c-global-basket__product-pack" htmlElement="span" text-type="body-sm" text-style="regular" data-testid="line-item-sample-quantity">
					{{ item.quantity }} {{ labels.samplePackLabel }}
				</DsText>

				<div
					class="c-global-basket__product-discount"
					v-if="item.discountDescription">
					<DsText htmlElement="div" text-type="body-sm">{{ item.discountDescription }}</DsText>
					<DsText htmlElement="div" text-type="body-sm">-{{ item.formattedSavedAmount }}</DsText>
				</div>
			</section>

			<!-- test case #6 -->
			<DsText v-if="showLineItemCuttingServiceSummary(item)" class="c-global-basket__summary-text" data-testid="cutting-service-summary">
				{{ labels.cuttingServiceSummaryLabel }}
			</DsText>

			<div class="c-global-basket__quantity-and-price">
				<!-- test case #7 -->
				<div v-if="showLineItemQuantitySection(item)" data-testid="line-item-quantity" class="c-global-basket__quantity">
					<span v-if="showLineItemFixedQuantityLabel(item)" data-testid="line-item-quantity-1-label">
						{{ quantityLabel }}: 1
					</span>
					<DsQuantitySelector
						v-if="showLineItemQuantitySelector()"
						v-model="quantityModelValue"
						:min="minQuantity"
						:max="computedMaxQuantity"
						@change="changeQuantity"
						:ariaLabelInput="quantitySelectorAriaLabel"
						:ariaLabelDecreaseQuantity="decreaseQuantityAriaLabel"
						:ariaLabelIncreaseQuantity="increaseQuantityAriaLabel"
						data-testid="line-item-quantity-selector"
						class="ds-quantity-selector"
					></DsQuantitySelector>

					<div class="c-global-basket__quantity-text-row">
						<DsText htmlElement="span" text-type="body-md" class="c-global-basket__quantity-text">
							{{ labels.quantityLabel }}
						</DsText>
						<DsText htmlElement="span" text-type="body-md" class="c-global-basket__quantity-text" data-testid="item-quantity-text">
							{{ item.quantity }}
						</DsText>
					</div>
				</div>

				<DsText class="c-global-basket__product-price" htmlElement="div" text-type="body-lg" text-style="bold">
					<!-- test case #8 -->
					<DsText htmlElement="span" text-type="body-lg" text-style="bold" class="c-global-basket__product-total" data-testid="line-item-price">
						{{ productPriceLabel }}<br />
					</DsText>
					<DsText v-if="showLinePriceTotal()" htmlElement="span" text-type="body-sm" text-style="normal" class="c-global-basket__line-price-total" data-testid="line-price-total">
						{{ labels.linePriceTotalLabel }} {{ item.formattedPrice }}
					</DsText>
				</DsText>
			</div>

			<kit-products
				class="c-global-basket__kit-products"
				:read-less="labels.hideProductKitComponentsLabel"
				:read-more="labels.showProductKitComponentsLabel"
				:items="item.productKitViewModel.productKitItemViewModels"
				:quantity-label="labels.quantityLabel"></kit-products>
		</div>

		<DsAlert
			v-if="maxQuantityReachedMessage"
			aria-live="assertive"
			appearance="highlight"
			:text="maxQuantityReachedMessage"
			class="max-quantity-alert"
		/>

		<div v-if="marketSpecificTax" class="c-global-basket__product-vat">
			<DsText htmlElement="span" text-type="body-xs">{{ labels.vatIsNotIncludedLabel }}</DsText>
		</div>

		<cutting-service-section v-if="isCuttingServiceAvailable && !isSummary"
			:item="item"
			:labels="labels.cuttingService"
			:accessibilityLabels="labels.accessibility" />
	</li>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import GalleryManufacturerImage from "shared/components/product/gallery/galleryManufacturerImage.vue";
	import { sampleAndPurchaseStates } from 'shared/settings/constants';
	import { debounce } from 'lodash';
	import cuttingServiceSection from './cuttingServiceSection.vue';
	import { DsText, DsIconButton, DsAlert, DsQuantitySelector } from '@coloplast/design-system';
	import BasketLineItemSkeleton from './skeletons/basketLineItemSkeleton.vue';
	import kitProducts from './kitProducts.vue';

	import "@coloplast/design-system/dist/components/quantity-selector/ds-quantity-selector.css";

	export default {
		name: 'basket-line-item',
		components: {
			GalleryManufacturerImage,
			cuttingServiceSection,
			DsText,
			DsIconButton,
			BasketLineItemSkeleton,
			kitProducts,
			DsAlert,
			DsQuantitySelector,
		},
		props: {
			item: {
				type: Object,
				required: true,
			},
			labels: {
				type: Object,
				required: true,
			},
			minQuantity: {
				type: Number,
				required: false,
				default: 1,
			},
			maxQuantity: {
				type: Number,
				required: false,
				default: 99,
			},
			isSample: {
				type: Boolean,
				default: false,
			},
			marketSpecificTax: {
				type: Boolean,
				default: false,
			},
			size: {
				type: String,
				default: 'normal'
			},
			isComplimentary: {
				type: Boolean,
				default: false,
			},
			isSummary: {
				type: Boolean,
				default: false,
			},
			quantityLabel: {
				type: String,
				default: ''
			},
			hideQuantity: {
				type: Boolean,
				default: false
			},
			maxTotalQuantityOfReimbursableItemsAllowed: {
				type: Number,
				default: Number.POSITIVE_INFINITY,
			},
			maxQuantityOfReimburseableItemsReached: {
				type: Boolean,
				default: false,
			},
			showPricesForReimbursableItems: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['updateQuantity'],
		data() {
			return {
				isDeleted: false,
			};
		},
		computed: {
			...mapGetters({
				visitorID: 'getVisitorID',
				currency: 'getCurrency',
				getBasketDebounceTime: 'getBasketDebounceTime',
				colors: 'getColors',
				basket: 'getBasket',
				totalQuantityOfReimbursableItems: 'getTotalQuantityOfReimbursableItems',
				globalSettings: 'getGlobalSettings',
			}),
			disableLinkToProductDetails() {
				return this.globalSettings.DisableRedirectToProductPageForSamples && this.isSample;
			},
			productDetailsLink() {
				return this.item.gatedProductDetailsEnabled ? this.item.gatedProductDetailUrl : this.item.productDetailUrl
			},
			quantityModelValue: {
				get() {
					return this.item.quantity;
				},
				set(newVal) {
					this.$emit('updateQuantity', newVal);
				},
			},
			maxQuantityBasedOnMaxTotalQuantityOfReimbursableItems() {
				if (this.item.isReimburseable && this.maxTotalQuantityOfReimbursableItemsAllowed > 0) {
					if (this.maxQuantityOfReimburseableItemsReached) {
						return this.item.quantity;
					}
					return this.item.quantity + this.maxTotalQuantityOfReimbursableItemsAllowed - this.totalQuantityOfReimbursableItems;
				}
				return Number.POSITIVE_INFINITY;
			},
			maxQuantityInCatalogue() {
				if (this.item.isReimburseable && this.item.maxOrderingQuantity > 0) {
					return this.item.maxOrderingQuantity;
				}
				return Number.POSITIVE_INFINITY;
			},
			maxOrderTemplateQuantity() {
				if (this.item.orderTemplateItemQuantity > 0) {
					return this.item.orderTemplateItemQuantity;
				}
				return Number.POSITIVE_INFINITY;
			},
			computedMaxQuantity() {
				return Math.min(
					this.maxOrderTemplateQuantity,
					this.maxQuantityBasedOnMaxTotalQuantityOfReimbursableItems,
					this.maxQuantityInCatalogue,
					this.maxQuantity
				);
			},
			maxQuantityReachedMessage() {
				if ((this.item.quantity < this.computedMaxQuantity) || this.maxQuantityOfReimburseableItemsReached) {
					return "";
				}
				if (this.item.quantity >= this.maxOrderTemplateQuantity) {
					return this.labels.orderTemplateItemsQuantityLimitReachedMessage;
				}
				return this.labels.maxLineItemOrderingQuantityReachedMessage;
			},
			additionalClasses() {
				return {
					'with-summary': this.withSummary,
					'with-kit': this.isKitProduct
				}
			},
			withSummary() {
				return this.isSummary && this.item.cuttingService?.isActive
			},
			isBuyable() {
				return !this.isSample && !this.isReimbursable && !this.isComplimentary;
			},
			packsOfLabel() {
				return this.item.quantity === 1
					? this.labels.packOfLabel
					: this.labels.packsOfLabel;
			},
			isSalesTaxIncluded() {
				if (this.marketSpecificTax) {
					return false;
				}
				return this.item.isSalesTaxIncluded && !this.isSample;
			},
			isReimbursable() {
				const conditions = [sampleAndPurchaseStates.IS_REIMBURSABLE, sampleAndPurchaseStates.IS_SAMPLE_AND_REIMBURSABLE];
				return conditions.includes(this.item.processIndicatorState);
			},
			manufactureInfo() {
				return {
					ManufactureIconUrl: this.item.manufactureIconUrl,
					ManufacturerName: this.item.manufacturer,
				}
			},
			isCuttingServiceAvailable() {
				return this.item.cuttingService?.isAvailable;
			},
			isKitProduct() {
				return this.item.productKitViewModel.isKit;
			},
			productPriceLabel() {
				if (this.isBuyable && !this.isComplimentary)
					return this.item.formattedPrice;
				else if (this.isComplimentary)
					return this.labels.complimentaryProductLabel;
				else if (this.isReimbursable && !this.isSample)
					return this.labels.reimbursedProductLabel;
				else if (this.item.productKitViewModel.isKit) {
					return this.item.productKitViewModel.productKitLabels.productKitPriceLabel;
				} else
					return this.labels.freeSampleText;
			},
			productNameAriaLabel() {
				return this.labels?.accessibility?.accessibilityProductLinkLabel?.replace('$product$', this.item.name) ?? this.item.name;
			},
			quantitySelectorAriaLabel() {
				return this.labels?.accessibility?.quantitySelectorLabel?.replace('$product$', this.item.name) ?? null;
			},
			increaseQuantityAriaLabel() {
				return this.labels?.accessibility?.increaseQuantityLabel?.replace('$product$', this.item.name) ?? null;
			},
			decreaseQuantityAriaLabel() {
				return this.labels?.accessibility?.decreaseQuantityLabel?.replace('$product$', this.item.name) ?? null;
			},
			removeItemAriaLabel() {
				return this.labels?.accessibility?.removeItem?.replace('$product$', this.item.name) ?? null;
			},
		},
		methods: {
			...mapActions([
				'removeItemFromBasket',
				'changeQuantityInBasket',
				'fetchVariants',
				'fetchBasket',
			]),
			showLineItemDescription(item) {
				// true if the item is not a product kit
				return !item.productKitViewModel?.isKit;
			},
			showLineItemColor(item) {
				// true if the color attribute has a value
				return item.color;
			},
			showLineItemPacksSection() {
				// true if this is not a sample
				return !this.isSample;
			},
			showLineItemPerPackQuantity() {
				return !(this.isSample || this.isComplimentary) || this.isKitProduct
			},
			showLineItemPerPackPrice() {
				// false if this is a sample or a complimentary product
				if (this.isSample || this.isComplimentary) {
					return false;
				}
				// true if the item is reimbursable and prices are to be shown for reimbursable products
				if (this.isReimbursable && this.showPricesForReimbursableItems) {
					return true;
				}
				// otherwise true if the item is not reimbursable
				return !this.isReimbursable;
			},
			showLineItemSalesTaxIncludedLabel(item) {
				// false if this is a sample or we have market specific tax
				if (this.isSample || this.marketSpecificTax) {
					return false;
				}
				return item.isSalesTaxIncluded
			},
			showLineItemSampleQuantity(item) {
				// false if this is not a sample
				if (!this.isSample) {
					return false;
				}
				// false if the item is a kit
				if (item.productKitViewModel?.isKit) {
					return false;
				}
				return true;
			},
			showLineItemCuttingServiceSummary(item) {
				// false if the isSummary prop is not true
				if (!this.isSummary) {
					return false;
				}
				// false if the item's cuttingService.isActive boolean is not true
				if (!item.cuttingService?.isActive) {
					return false;
				}
				return true;
			},
			showLineItemQuantitySection(item) {
				// false if the hideQuantity prop is true
				if (this.hideQuantity) {
					return false;
				}
				// false if this is a product kit
				if (item.productKitViewModel?.isKit) {
					return false;
				}
				return true;
			},
			showLineItemFixedQuantityLabel() {
				// false if this is a sample
				if (this.isSample) {
					return false;
				}
				// true if the maxQuantity prop is exactly 1
				if (this.maxQuantity === 1) {
					return true;
				}
				return false;
			},
			showLineItemQuantitySelector() {
				// false if this is a sample
				if (this.isSample) {
					return false;
				}
				// true if the maxQuantity prop is > 1
				if (this.maxQuantity > 1) {
					return true;
				}
				return false;
			},
			showLinePriceTotal() {
				if (this.isSample || this.isComplimentary) {
					return false;
				}
				return this.isReimbursable && this.showPricesForReimbursableItems;
			},
			removeItem() {
				this.isDeleted = true;
				return this.removeItemFromBasket({
						code: this.item.code,
						isSample: this.isSample,
						lineItemNumber: this.item.lineItemNumber,
						isComplimentary: this.isComplimentary,
					}).then(() => {
						this.fetchVariants();
					}).catch(() => {
						this.isDeleted = false;
					});

			},
			changeQuantity(quantity) {
				this.debouncedChangeQuantity(quantity);
			},
			async handleChangeQuantity(quantity) {
				await this.changeQuantityInBasket({
					code: this.item.code,
					lineItemNumber: this.item.lineItemNumber,
					quantity,
					isSample: this.isSample,
				});
			},
		},
		async mounted() {
			this.debouncedChangeQuantity = debounce((quantity) => this.handleChangeQuantity(quantity), this.getBasketDebounceTime);
			await this.fetchBasket();
		},
	};
</script>

<style scoped>
li.c-basket-line-item {
	width: 100%;
}

.max-quantity-alert {
	width: 100%;
}

</style>
